import React, { useEffect, useState } from 'react';
import { Plus, Shield, Building2, Users as UsersIcon, Trash2, Ban, CheckCircle, Edit2, X, Loader2, RefreshCw, MapPin } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { usersApi, teamsApi, rolesApi, UserRecord, TeamRecord, RoleRecord } from '../services/creatorApi';
import { toast } from 'sonner';
export function SuperAdminSettings() {
  const {
    currentUser,
    workspaces,
    roles,
    refreshTeams
  } = useWorkspace();
  const [allUsers, setAllUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'users' | 'workspaces'>('users');
  const [selectedTeamFilter, setSelectedTeamFilter] = useState<string>('all');
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  // Security check
  if (currentUser.Role !== 'Super Admin') {
    return <div className="p-8 text-center text-red-600" data-id="element-4677">
        Access Denied: Super Admin privileges required.
      </div>;
  }
  const loadUsers = async () => {
    setLoading(true);
    try {
      const users = await usersApi.list();
      setAllUsers(users);
    } catch (err) {
      console.error('Failed to load users:', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadUsers();
  }, []);
  const filteredUsers = selectedTeamFilter === 'all' ? allUsers : allUsers.filter(u => u.Team === selectedTeamFilter);
  const handleToggleStatus = async (user: UserRecord) => {
    const newStatus = user.Status === 'Active' ? 'Suspended' : 'Active';
    try {
      await usersApi.update(user.ID, {
        Status: newStatus
      });
      toast.success(`User ${newStatus === 'Active' ? 'activated' : 'suspended'}`);
      loadUsers();
    } catch (err) {
      toast.error('Failed to update user status');
    }
  };
  const handleDeleteUser = async (user: UserRecord) => {
    if (!confirm(`Delete ${user.User_Name}?`)) return;
    try {
      await usersApi.delete(user.ID);
      toast.success('User deleted');
      loadUsers();
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };
  const handleStartEditRole = (userId: string, currentRole: string) => {
    setEditingUserId(userId);
    setSelectedRole(currentRole);
  };
  const handleSaveRole = async (userId: string) => {
    try {
      await usersApi.update(userId, {
        Role: selectedRole as any
      });
      toast.success('Role updated');
      setEditingUserId(null);
      loadUsers();
    } catch (err) {
      toast.error('Failed to update role');
    }
  };
  const handleCreateTeam = async () => {
    const name = prompt('Enter team name:');
    if (!name?.trim()) return;
    try {
      await teamsApi.create({
        Team_Name: name.trim(),
        Team_ID: teamsApi.generateTeamId(name.trim())
      });
      toast.success('Team created');
      refreshTeams();
    } catch (err) {
      toast.error('Failed to create team');
    }
  };
  const getRoleBadgeColor = (roleName: string) => {
    switch (roleName) {
      case 'Super Admin':
        return 'bg-purple-100 text-purple-800';
      case 'Team Manager':
        return 'bg-blue-100 text-blue-800';
      case 'Operator':
        return 'bg-green-100 text-green-800';
      case 'Viewer':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const activeUsers = allUsers.filter(u => u.Status === 'Active');
  return <div className="space-y-6" data-id="element-4678">
      <div className="flex items-center justify-between" data-id="element-4679">
        <div data-id="element-4680">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4681">
            Super Admin Settings
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-4682">
            Global management of users and teams across all workspaces.
          </p>
        </div>
        <button onClick={loadUsers} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Refresh" data-id="element-4683">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-4684" />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4" data-id="element-4685">
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-4686">
          <div className="flex items-center gap-3" data-id="element-4687">
            <div className="p-2 bg-blue-50 rounded-lg" data-id="element-4688">
              <UsersIcon className="h-5 w-5 text-blue-600" data-id="element-4689" />
            </div>
            <div data-id="element-4690">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-4691">
                {allUsers.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-4692">Total Users</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-4693">
          <div className="flex items-center gap-3" data-id="element-4694">
            <div className="p-2 bg-green-50 rounded-lg" data-id="element-4695">
              <Building2 className="h-5 w-5 text-green-600" data-id="element-4696" />
            </div>
            <div data-id="element-4697">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-4698">
                {workspaces.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-4699">Teams</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-4700">
          <div className="flex items-center gap-3" data-id="element-4701">
            <div className="p-2 bg-purple-50 rounded-lg" data-id="element-4702">
              <Shield className="h-5 w-5 text-purple-600" data-id="element-4703" />
            </div>
            <div data-id="element-4704">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-4705">
                {roles.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-4706">Roles</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-4707">
          <div className="flex items-center gap-3" data-id="element-4708">
            <div className="p-2 bg-yellow-50 rounded-lg" data-id="element-4709">
              <UsersIcon className="h-5 w-5 text-yellow-600" data-id="element-4710" />
            </div>
            <div data-id="element-4711">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-4712">
                {activeUsers.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-4713">Active Users</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-4714">
        <nav className="-mb-px flex space-x-8" data-id="element-4715">
          <button onClick={() => setActiveTab('users')} className={`${activeTab === 'users' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`} data-id="element-4716">
            <UsersIcon className="h-4 w-4" data-id="element-4717" />
            All Users ({allUsers.length})
          </button>
          <button onClick={() => setActiveTab('workspaces')} className={`${activeTab === 'workspaces' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`} data-id="element-4718">
            <Building2 className="h-4 w-4" data-id="element-4719" />
            Teams ({workspaces.length})
          </button>
        </nav>
      </div>

      {activeTab === 'users' && <div className="space-y-4" data-id="element-4720">
          <div className="flex justify-between items-center" data-id="element-4721">
            <div className="flex items-center gap-2" data-id="element-4722">
              <span className="text-sm text-gray-500" data-id="element-4723">Filter by Team:</span>
              <select className="rounded-md border-gray-300 text-sm focus:ring-blue-500 focus:border-blue-500" value={selectedTeamFilter} onChange={e => setSelectedTeamFilter(e.target.value)} data-id="element-4724">
                <option value="all" data-id="element-4725">All Teams ({allUsers.length} users)</option>
                {workspaces.map(ws => <option key={ws.id} value={ws.name} data-id="element-4726">
                    {ws.name} (
                    {allUsers.filter(u => u.Team === ws.name).length} users)
                  </option>)}
              </select>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-4727">
            {loading ? <div className="p-12 text-center" data-id="element-4728">
                <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-4729" />
                <p className="mt-2 text-sm text-gray-500" data-id="element-4730">Loading users...</p>
              </div> : filteredUsers.length === 0 ? <div className="p-12 text-center" data-id="element-4731">
                <UsersIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-4732" />
                <p className="mt-2 text-sm text-gray-500" data-id="element-4733">No users found</p>
              </div> : <table className="min-w-full divide-y divide-gray-200" data-id="element-4734">
                <thead className="bg-gray-50" data-id="element-4735">
                  <tr data-id="element-4736">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4737">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4738">
                      Team
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4739">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4740">
                      Regions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4741">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4742">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-4743">
                  {filteredUsers.map(user => <tr key={user.ID} className="hover:bg-gray-50" data-id="element-4744">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-4745">
                        <div className="flex items-center" data-id="element-4746">
                          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-xs font-medium text-white mr-3" data-id="element-4747">
                            {user.User_Name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div data-id="element-4748">
                            <div className="text-sm font-medium text-gray-900" data-id="element-4749">
                              {user.User_Name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-4750">
                              {user.Email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-4751">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-4752">
                          {user.Team}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-4753">
                        {editingUserId === user.ID ? <div className="flex items-center gap-2" data-id="element-4754">
                            <select className="rounded-md border-gray-300 text-sm" value={selectedRole} onChange={e => setSelectedRole(e.target.value)} data-id="element-4755">
                              <option value="Super Admin" data-id="element-4756">Super Admin</option>
                              <option value="Team Manager" data-id="element-4757">Team Manager</option>
                              <option value="Operator" data-id="element-4758">Operator</option>
                              <option value="Viewer" data-id="element-4759">Viewer</option>
                            </select>
                            <button onClick={() => handleSaveRole(user.ID)} className="p-1 text-green-600 hover:text-green-800" data-id="element-4760">
                              <CheckCircle className="h-4 w-4" data-id="element-4761" />
                            </button>
                            <button onClick={() => setEditingUserId(null)} className="p-1 text-gray-400 hover:text-gray-600" data-id="element-4762">
                              <X className="h-4 w-4" data-id="element-4763" />
                            </button>
                          </div> : <div className="flex items-center gap-2" data-id="element-4764">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(user.Role)}`} data-id="element-4765">
                              {user.Role}
                            </span>
                            {user.ID !== currentUser.ID && <button onClick={() => handleStartEditRole(user.ID, user.Role)} className="p-1 text-gray-400 hover:text-blue-600" title="Change Role" data-id="element-4766">
                                <Edit2 className="h-3 w-3" data-id="element-4767" />
                              </button>}
                          </div>}
                      </td>
                      <td className="px-6 py-4" data-id="element-4768">
                        <div className="flex flex-wrap gap-1 max-w-[150px]" data-id="element-4769">
                          {user.User_Regions && user.User_Regions.length > 0 ? user.User_Regions.map((r, i) => <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-700" data-id="element-4770">
                                {r.Region_Name}
                              </span>) : <span className="text-xs text-gray-400 italic" data-id="element-4771">
                              All
                            </span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-4772">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${user.Status === 'Active' ? 'bg-green-100 text-green-800' : user.Status === 'Suspended' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-4773">
                          {user.Status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-4774">
                        {user.ID !== currentUser.ID && <div className="flex items-center justify-end gap-2" data-id="element-4775">
                            {user.Status === 'Active' ? <button onClick={() => handleToggleStatus(user)} className="text-yellow-600 hover:text-yellow-900" title="Suspend" data-id="element-4776">
                                <Ban className="h-4 w-4" data-id="element-4777" />
                              </button> : <button onClick={() => handleToggleStatus(user)} className="text-green-600 hover:text-green-900" title="Activate" data-id="element-4778">
                                <CheckCircle className="h-4 w-4" data-id="element-4779" />
                              </button>}
                            <button onClick={() => handleDeleteUser(user)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-4780">
                              <Trash2 className="h-4 w-4" data-id="element-4781" />
                            </button>
                          </div>}
                      </td>
                    </tr>)}
                </tbody>
              </table>}
          </div>
        </div>}

      {activeTab === 'workspaces' && <div className="space-y-4" data-id="element-4782">
          <div className="flex justify-end" data-id="element-4783">
            <button onClick={handleCreateTeam} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-4784">
              <Plus className="h-4 w-4" data-id="element-4785" />
              Create Team
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-4786">
            {workspaces.map(ws => <div key={ws.id} className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-4787">
                <div className="flex items-start justify-between mb-4" data-id="element-4788">
                  <div className="p-2 bg-blue-50 rounded-lg" data-id="element-4789">
                    <Building2 className="h-6 w-6 text-blue-600" data-id="element-4790" />
                  </div>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-4791">
                  {ws.name}
                </h3>
                <p className="text-sm text-gray-500 mb-4" data-id="element-4792">ID: {ws.id}</p>
                <div className="mt-4 space-y-2" data-id="element-4793">
                  <div className="flex justify-between text-sm" data-id="element-4794">
                    <span className="text-gray-500" data-id="element-4795">Users</span>
                    <span className="font-medium text-gray-900" data-id="element-4796">
                      {allUsers.filter(u => u.Team === ws.name).length}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-4797">
                    <span className="text-gray-500" data-id="element-4798">Regions</span>
                    <span className="font-medium text-gray-900" data-id="element-4799">
                      {ws.subRegions.length}
                    </span>
                  </div>
                  {ws.subRegions.length > 0 && <div className="flex flex-wrap gap-1 pt-1" data-id="element-4800">
                      {ws.subRegions.map(r => <span key={r} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700" data-id="element-4801">
                          <MapPin className="h-3 w-3" data-id="element-4802" />
                          {r}
                        </span>)}
                    </div>}
                </div>
              </div>)}
          </div>
        </div>}
    </div>;
}